import { useState, useEffect, useCallback } from 'react'

import type { AgentStatus } from '../components/AgentStatusToggle'

const INACTIVITY_TIMEOUT = 5 * 60 * 1000 // 5 minutes
const STATUS_STORAGE_KEY = 'agent_status'

export const useAgentStatus = () => {
  const [status, setStatus] = useState<AgentStatus>(() => {
    const stored = localStorage.getItem(STATUS_STORAGE_KEY)
    return (stored as AgentStatus) || 'offline'
  })
  const [lastActivity, setLastActivity] = useState(() => Date.now())

  // Save status to localStorage
  useEffect(() => {
    localStorage.setItem(STATUS_STORAGE_KEY, status)
  }, [status])

  // Track user activity
  const handleActivity = useCallback(() => {
    setLastActivity(Date.now())
    
    // If user was away, set them back to online
    if (status === 'away') {
      setStatus('online')
    }
  }, [status])

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    
    events.forEach((event) => {
      window.addEventListener(event, handleActivity)
    })

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
    }
  }, [handleActivity])

  // Auto-away functionality
  useEffect(() => {
    if (status !== 'online') return

    const interval = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivity
      
      if (timeSinceActivity >= INACTIVITY_TIMEOUT) {
        setStatus('away')
      }
    }, 30000) // Check every 30 seconds

    return () => clearInterval(interval)
  }, [status, lastActivity])

  const updateStatus = useCallback((newStatus: AgentStatus) => {
    setStatus(newStatus)
    setLastActivity(Date.now())
  }, [])

  return {
    status,
    updateStatus,
  }
}
